<template>
  <div class="keywords-list">
    <KeywordsPhoneCategory
      v-if="isPhone"
      :category="category"
      :keywords="keywords"
      :limit="itemsPerPage"
      @add="onKeywordsAdd"
      @edit-category="onCategoryEdit"
      @delete-category="onCategoryDelete"
      @edit-keyword="onKeywordsEdit"
      @delete-keywords="onPhoneDelete"
    />
    <KeywordsStatusManager
      v-if="isPhone"
      ref="phoneDelete"
      :keywords="selectedKeywords"
      :has-button="false"
      @keywords-status-updated="unselectAll"
    />
    <template v-else>
      <div class="title">
        {{ category.name }}
        <div class="keywords-list__buttons">
          <BaseRoundButton
            @click.native="onCategoryEdit"
            :size="40"
            :tooltip-text="$t('actions.edit')"
            class="keywords-list__button"
            icon="edit-2"
            is-white
          />
          <BaseRoundButton
            @click.native="onCategoryDelete"
            :size="40"
            :tooltip-text="$t('actions.delete')"
            class="keywords-list__button"
            icon="trash-2"
            is-red
            is-white
          />
        </div>
      </div>
      <TableNormal
        v-model="selectedKeywords"
        :fixed-header="true"
        :headers="headers"
        :items="keywords"
        :items-per-page="itemsToShow"
        :single-select="false"
        hide-default-footer
        item-key="value"
        show-select
        class="admin-keywords-table"
        v-if="keywords.length"
      >
        <template v-slot:header.data-table-select="{}">
          <FormCheckbox
            :indeterminate="partiallySelected"
            :afterIndeterminate="false"
            :onChange="onSelectAllClick"
            :value="allAreSelected"
            class="keywords-list__check-all"
          />
          <KeywordsStatusManager
            v-if="hasSelection"
            :keywords="selectedKeywords"
            @keywords-status-updated="unselectAll"
          />
        </template>
        <template v-slot:header.actions>
          <BaseButton @click.native="onKeywordsAdd" isSmall class="keywords-list__add-keyword" has-icon-left>
            <BaseIcon name="plus" />
            {{ $t('page.admin-keywords.add-keyword') }}
          </BaseButton>
        </template>
        <template v-slot:item.data-table-select="{ isSelected, select }">
          <FormCheckbox :value="isSelected" @input="select($event)" @click.native.stop />
        </template>
        <template v-slot:item.title="{ item }">
          <Keyword>
            {{ item.value }}
          </Keyword>
        </template>
        <template v-slot:item.actions="{ item }">
          <BaseRoundButton
            :size="32"
            :tooltip-text="$t('actions.edit')"
            icon="edit-2"
            is-white
            class="keywords-list__edit-keyword"
            @click.native.stop="onKeywordsEdit(item)"
        /></template>
      </TableNormal>
      <div v-else class="keywords-list__empty-keywords">
        <BaseButton @click.native="onKeywordsAdd" class="keywords-list__empty-add-keyword" isSmall has-icon-left>
          <BaseIcon name="plus" />
          {{ $t('page.admin-keywords.add-keyword') }}
        </BaseButton>
      </div>
      <div v-if="keywords.length > itemsPerPage" ref="tendersBottomList">
        <span @click="toggleKeywordsList" class="keywords-list__show-all">
          {{ showAll ? $t('dialog.keywords.hide') : $t('dialog.keywords.show-more', { count: itemsLeft }) }}
          <BaseIcon v-if="showAll" name="chevron-up"></BaseIcon>
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import FormCheckbox from '@/components/form/FormCheckbox.vue';
import Keyword from '@/components/page-keywords/Keyword';
import KeywordsPhoneCategory from '@/components/page-admin-keywords/KeywordsPhoneCategory.vue';
import KeywordsStatusManager from '@/components/page-admin-keywords/KeywordsStatusManager.vue';
import TableNormal from '@/components/table/TableNormal.vue';

import { mapGetters } from 'vuex';
import { viewport } from '@/util/viewport';

export default {
  name: 'KeywordsList',
  components: {
    BaseButton,
    BaseIcon,
    BaseRoundButton,
    FormCheckbox,
    Keyword,
    KeywordsPhoneCategory,
    KeywordsStatusManager,
    TableNormal,
  },
  props: {
    category: {
      type: Object,
    },
  },
  data() {
    return {
      selectedKeywords: [],
      itemsPerPage: 8,
      showAll: false,
    };
  },
  computed: {
    ...mapGetters('keywords', ['categories']),
    isPhone() {
      return viewport.size === 'phone';
    },
    keywords() {
      return this.categories.filter((cat) => cat.id === this.category.id)[0].keywords;
    },
    itemsToShow() {
      return this.showAll ? -1 : this.itemsPerPage;
    },
    itemsLeft() {
      return this.keywords.length - this.itemsPerPage;
    },
    headers() {
      return [
        {
          text: this.$t('menu.keywords'),
          align: 'left',
          sortable: false,
          value: 'title',
          class: 'fixed-size-l',
        },
        {
          text: '',
          align: 'right',
          sortable: false,
          value: 'actions',
        },
      ];
    },
    hasSelection() {
      return this.selectedKeywords.length > 0;
    },
    hasKewyords() {
      return this.keywords.length > 0;
    },
    allAreSelected() {
      return this.hasKewyords && this.keywords.length === this.selectedKeywords.length;
    },
    partiallySelected() {
      return this.hasKewyords && this.hasSelection && this.keywords.length !== this.selectedKeywords.length;
    },
  },
  methods: {
    toggleKeywordsList() {
      this.showAll = !this.showAll;
    },
    onCategoryEdit() {
      this.$emit('on-category-edit', this.category);
    },
    onCategoryDelete() {
      this.$emit('on-category-delete', this.category);
    },
    onKeywordsAdd() {
      const anchor = this.$refs.tendersBottomList;
      if (anchor) anchor.scrollIntoView({ block: 'center', behavior: 'smooth', inline: 'start' });
      this.$emit('on-add-keyword', this.category);
    },
    onKeywordsEdit(item) {
      this.$emit('on-keyword-edit', item);
    },
    onSelectAllClick() {
      if (this.partiallySelected || this.allAreSelected) this.unselectAll();
      else this.selectAll();
    },
    selectAll() {
      this.selectedKeywords = [...this.keywords];
    },
    unselectAll() {
      this.selectedKeywords = [];
    },
    // Phones: the confirmation lists the keywords picked in the category (one, or a selection).
    onPhoneDelete(keywords) {
      this.selectedKeywords = keywords;
      this.$refs.phoneDelete.onDeleteKeywords();
    },
  },
};
</script>

<style lang="scss">
.keywords-list {
  .title {
    border-bottom: solid 1px $color-border;
    color: $color-normal;
    font-size: 25px;
    font-weight: 800;
    margin-bottom: 20px;
    padding-bottom: 14px;
    display: flex;
  }

  &__buttons {
    display: flex;
    justify-content: flex-end;
    margin-left: auto;
  }

  &__button {
    padding-left: 4px;
  }

  &__list {
    display: flex;
    align-items: center;
    padding: 4px 0;
  }

  &__check-all {
    z-index: $third-index;
  }

  &__add-keyword {
    position: absolute;
    left: -68px;
    top: 18px;
  }

  &__edit-keyword {
    padding-right: 8px;
  }

  &__show-all {
    display: flex;
    text-decoration: underline;
    padding-bottom: 16px;
    font-size: 14px;
    color: $color-neutral;
    cursor: pointer;
    transition: color 200ms ease;

    .base-icon {
      height: 15px;
      width: 15px;
      align-content: center;
      padding-left: 2px;
    }

    &:hover {
      color: $color-normal;
      transition: color 200ms ease;
    }
  }

  &__empty-keywords {
    height: 250px;
    border: 1px solid $color-border;
    border-style: dashed;
    border-radius: $border-radius-medium;
    align-items: center;
    display: flex;
    margin-bottom: 16px;
  }

  &__empty-add-keyword {
    display: flex;
    justify-content: center;
    margin: auto;
  }
}

.admin-keywords-table {
  padding-bottom: 16px !important;

  tbody {
    cursor: default !important;

    td {
      padding: 4px 0px 4px 0px !important;

      &:first-child {
        padding-left: 16px !important;
      }
    }
  }

  thead {
    tr {
      th:nth-child(2) {
        padding-left: 0px !important;
      }
    }
  }

  .v-data-table-header {
    box-shadow: none !important;
  }

  .v-data-table-header tr th {
    padding: 16px 16px 18px 16px !important;

    &.fixed-size-l {
      width: 90%;
    }
  }
}
</style>
